import React, { useEffect, useRef } from "react";
import ApexCharts, { ApexOptions } from "apexcharts";
import { getCSS, getCSSVariableValue } from "../../../../_metronic/assets/ts/_utils";
import { useThemeMode } from "../../../../_metronic/partials";

export interface CombinedData {
    currentTitle: string;
    currentPeriod: Array<{ date: string; value: number }>;
}

const dummyData: CombinedData[] = [
    {
        currentTitle: "Current Period 1",
        currentPeriod: [
            { date: "2024-01-01", value: 10 },
            { date: "2024-01-02", value: 15 },
            { date: "2024-01-03", value: 20 },
            { date: "2024-01-04", value: 25 },
            { date: "2024-01-05", value: 30 },
        ],
    },
    {
        currentTitle: "Current Period 2",
        currentPeriod: [
            { date: "2024-01-01", value: 20 },
            { date: "2024-01-02", value: 25 },
            { date: "2024-01-03", value: 30 },
            { date: "2024-01-04", value: 35 },
            { date: "2024-01-05", value: 40 },
        ],
    },
    {
        currentTitle: "Current Period 3",
        currentPeriod: [
            { date: "2024-01-01", value: 5 },
            { date: "2024-01-02", value: 10 },
            { date: "2024-01-03", value: 15 },
            { date: "2024-01-04", value: 20 },
            { date: "2024-01-05", value: 25 },
        ],
    },
    {
        currentTitle: "Current Period 4",
        currentPeriod: [
            { date: "2024-01-01", value: 15 },
            { date: "2024-01-02", value: 20 },
            { date: "2024-01-03", value: 25 },
            { date: "2024-01-04", value: 30 },
            { date: "2024-01-05", value: 35 },
        ],
    },
    {
        currentTitle: "Current Period 5",
        currentPeriod: [
            { date: "2024-01-01", value: 25 },
            { date: "2024-01-02", value: 30 },
            { date: "2024-01-03", value: 35 },
            { date: "2024-01-04", value: 40 },
            { date: "2024-01-05", value: 45 },
        ],
    },
];

type Props = {
    className: string;
    compareData?: CombinedData[]; // Changed to an array
};

const LineChartWithDataWaterfall: React.FC<Props> = ({
    className,
    compareData = dummyData, // Use dummy data if none is provided
}: Props) => {
    const chartRef = useRef<HTMLDivElement | null>(null);
    const { mode } = useThemeMode();

    const refreshMode = () => {
        if (!chartRef.current) {
            return;
        }

        const height = parseInt(getCSS(chartRef.current, "height"));

        const chart = new ApexCharts(
            chartRef.current,
            getChartOptions(height, compareData)
        );
        if (chart) {
            chart.render();
        }

        return chart;
    };

    useEffect(() => {
        const chart = refreshMode();

        return () => {
            if (chart) {
                chart.destroy();
            }
        };
    }, [chartRef, mode]); // eslint-disable-line 

    return (
        <div
            ref={chartRef}
            id="kt_charts_widget_3_chart"
            style={{ height: "300px", width: "100%" }} // Increased height for visibility
        ></div>
    );
};

export { LineChartWithDataWaterfall };

function getChartOptions(height: number, compareData: CombinedData[]): ApexOptions {
    const labelColor = getCSSVariableValue("--kt-gray-500");
    const borderColor = getCSSVariableValue("--kt-gray-200");

    return {
        series: compareData.map(data => ({
            name: data.currentTitle,
            data: data.currentPeriod.map(el => el.value),
        })),
        chart: {
            height: 300,
            type: "line",
            toolbar: {
                show: false,
            },
        },
        dataLabels: {
            enabled: false,
        },
        legend: {
            show: false, // Enable legend to distinguish between lines
        },
        stroke: {
            curve: "smooth",
            width: 2,
        },
        grid: {
            borderColor: borderColor,
            strokeDashArray: 4,
        },
        xaxis: {
            categories: compareData[0]?.currentPeriod.map((el, index) => (index + 1) * 50000) || [],  // Numbers instead of dates
            axisBorder: {
                show: false,
            },
            labels: {
                style: {
                    colors: labelColor,
                },
            },
        },
        yaxis: {
            labels: {
                style: {
                    colors: labelColor,
                },
                formatter: function (val: number) {
                    if (val >= 1000000000) return (val / 1000000000).toFixed(1) + 'B';
                    if (val >= 1000000) return (val / 1000000).toFixed(1) + 'M';
                    return val.toString(); // Ensure everything is returned as a string
                },
            },
        },
    };
}